import {
  GetProductsByIdBatchedQuery,
  WishlistList,
  VtoProductBasic,
} from '@notino/shared/definitions/types';
import { AdditionalServicesAvailability } from '@notino/web-tracking';
import { Product } from '@notino/web-tracking/dist/types/package/ga4/events/products';

import { getRenderableDiscoBoxSampleId } from '@containers/ProductDetailContainer/ProductDetail/ProductBaseInfo/DiscoveryBoxSelfChoice';
import { useProductDetailContext } from '@containers/ProductDetailContainer/ProductDetailContext';
import { trackWishlistChange } from '@containers/WishList/tracking';
import { FeatureFlags } from '@context/featureFlags/FeatureFlagsProvider';
import { dispatchTrackingEvent } from '@context/tracking/utils';
import { ProductEventWither } from '@helpers/googleTagManager';
import { getWishlistTotalCount } from '@lib/wishlist/getWishlistTotalCount';

export type WishlistActionLocation = 'product_detail' | 'wishlist' | 'list';

interface HandleTrackWishlistOperationParams {
  wishlisted: boolean;
  shouldShowSkinAnalyzer: boolean;
  vpProducts?: GetProductsByIdBatchedQuery['vpProductByIds'][number][];
  vtoProducts?: VtoProductBasic[];
  product?: ReturnType<typeof useProductDetailContext>['product'];
  variant?: ReturnType<typeof useProductDetailContext>['currentVariant'];
  timing: number;
  actionLocation: WishlistActionLocation;
  additionalPromoLabels?: string[];
  additionalGtm?: Partial<Product>;
  flags: FeatureFlags;
  engravingAvailable: AdditionalServicesAvailability;
  tryItFirstAvailable: AdditionalServicesAvailability;
  lists: WishlistList[];
  addToLists: { id: string }[];
  removeFromLists: { id: string }[];
  productCount?: number;
  selectionMode?: boolean;
  interaction: 'product_add' | 'remove_button' | 'move_button';
}

export const handleTrackWishlistOperation = ({
  wishlisted,
  vpProducts,
  vtoProducts,
  product,
  variant,
  timing,
  actionLocation,
  additionalGtm = {},
  additionalPromoLabels = [],
  flags,
  engravingAvailable,
  tryItFirstAvailable,
  shouldShowSkinAnalyzer,
  addToLists,
  productCount,
  removeFromLists,
  selectionMode,
  lists,
  interaction,
}: HandleTrackWishlistOperationParams) => {
  const additionalData: Partial<Product> = Object.entries(additionalGtm).reduce(
    (acc, [key, value]) =>
      value === undefined ? acc : { ...acc, [key]: value },
    {}
  );

  const baseWither = (
    vpProduct?: GetProductsByIdBatchedQuery['vpProductByIds'][number],
    vtoProduct?: VtoProductBasic
  ) => {
    const wither = ProductEventWither()
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .withVpProduct(
        vpProduct ? ({ ...vpProduct, wishlisted } as any) : undefined
      )
      .withVtoProduct(vtoProduct ? { ...vtoProduct, wishlisted } : undefined)
      .withProduct((product ?? undefined) as any)
      .withVariant(variant ? ({ ...variant, wishlisted } as any) : undefined)
      .withDiscountPrices(
        variant ? { ...variant.attributes, wishlisted } : undefined
      )
      .withAdditionalPromoLabels(additionalPromoLabels)
      .withAdditionalData(additionalData);

    if (product && variant && product.features) {
      wither.withServices({
        engravingAvailable,
        tryItFirstAvailable,
        skinAnalyzer: shouldShowSkinAnalyzer,
        discoveryBoxAvailable: Boolean(
          getRenderableDiscoBoxSampleId(
            product,
            variant,
            flags as Pick<FeatureFlags, 'discovery_box_self_choice_enabled'>
          )
        ),
      });
    }

    return wither.build();
  };

  // Create tracking products from VP products and VTO products separately
  const vpTrackingProducts = (vpProducts || []).map((vpProduct) =>
    baseWither(vpProduct)
  );
  const vtoTrackingProducts = (vtoProducts || []).map((vtoProduct) =>
    baseWither(undefined, vtoProduct)
  );

  const trackingProducts = [
    ...vpTrackingProducts,
    ...vtoTrackingProducts,
    ...(vpProducts?.length || vtoProducts?.length ? [] : [baseWither()]),
  ];

  const _productIds = [
    variant?.webId,
    ...(vpProducts || [])
      .filter((p): p is NonNullable<typeof p> => Boolean(p))
      .map((p) => p.id),
    ...(vtoProducts || []).map((p) => p.webProductId),
  ]
    .filter(Boolean)
    .map(Number);

  const wasInWishlistBefore = _productIds.every((id) =>
    lists.some((list) => list.products?.includes(id))
  );

  if (wasInWishlistBefore && wishlisted) {
    trackWishlistChange({
      products: trackingProducts.map(({ product_code }) => ({
        product_code,
      })),
      interaction,
    });
    return;
  }

  if (wishlisted) {
    dispatchTrackingEvent({
      event: 'add_to_wishlist',
      wishlist: {
        action: 'product_added_to_wishlist',
        interaction: 'click_add',
        type: actionLocation,
        timing,
        products: trackingProducts,
        number_of_wishlists: addToLists.length,
        wishlist_total_lists: lists.length,
        wishlist_total_products: getWishlistTotalCount({
          lists,
          webIds: _productIds,
          addedToLists: addToLists,
          removeFromLists,
        }),
      },
      _clear: true,
    });
  } else {
    dispatchTrackingEvent({
      event: 'remove_from_wishlist',
      wishlist: {
        action: 'product_removed_from_wishlist',
        interaction: selectionMode ? 'product_remove' : 'click_remove',
        type: actionLocation,
        timing,
        products: trackingProducts,
        product_count: productCount,
        wishlist_total_lists: lists.length,
        wishlist_total_products: getWishlistTotalCount({
          lists,
          webIds: _productIds,
          addedToLists: addToLists,
          removeFromLists,
        }),
      },
      _clear: true,
    });
  }
};
